import React, { useState, useRef } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { DateTime } from 'luxon';

const API = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`;

// Browser default timezone guess (user can override)
const guessTimezone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
};

// Common timezones offered in the dropdown
const COMMON_TIMEZONES = [
  'UTC',
  'Africa/Johannesburg',
  'Africa/Windhoek',
  'Africa/Maputo',
  'Europe/Berlin',
  'Europe/London',
  'Europe/Zurich',
  'America/New_York',
  'America/Los_Angeles',
  'Asia/Dubai',
  'Asia/Singapore',
  'Australia/Sydney',
];

const ALLOWED_EXTENSIONS = ['.csv', '.tsv', '.txt', '.xlsx', '.ods'];

// --- Parsing helpers --------------------------------------------------------

const readCsvFile = (file) => new Promise((resolve, reject) => {
  Papa.parse(file, {
    skipEmptyLines: true,
    complete: (res) => resolve(res.data),
    error: (err) => reject(err),
  });
});

const readSpreadsheetFile = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const wb = XLSX.read(e.target.result, { type: 'array', cellDates: true });
      const firstSheetName = wb.SheetNames[0];
      const sheet = wb.Sheets[firstSheetName];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, blankrows: false });
      resolve(rows);
    } catch (err) {
      reject(err);
    }
  };
  reader.onerror = reject;
  reader.readAsArrayBuffer(file);
});

// Parse a single value as a Date (best effort — handles ISO, "YYYY-MM-DD HH:MM", etc.)
const parseDateMaybe = (v) => {
  if (v == null || v === '') return null;
  if (v instanceof Date && !isNaN(v)) return v;
  const s = String(v).trim();
  // Try native Date parser first
  let d = new Date(s);
  if (!isNaN(d)) return d;
  // Try "DD/MM/YYYY HH:MM" or "DD.MM.YYYY HH:MM"
  const m = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
  if (m) {
    const [, dd, mm, yyyy, hh = '0', mi = '0', ss = '0'] = m;
    d = new Date(Number(yyyy), Number(mm) - 1, Number(dd), Number(hh), Number(mi), Number(ss));
    if (!isNaN(d)) return d;
  }
  return null;
};

const parseNumberMaybe = (v) => {
  if (v == null || v === '') return NaN;
  if (typeof v === 'number') return v;
  const s = String(v).trim().replace(',', '.');
  return parseFloat(s);
};

// Detect header, column layout, interval, gaps, stats
const analyzeRows = (rows) => {
  const errors = [];
  const warnings = [];
  if (!rows || rows.length < 2) {
    errors.push('File has fewer than 2 rows');
    return { ok: false, errors, warnings };
  }

  // Drop empty leading rows
  const nonEmpty = rows.filter((r) => Array.isArray(r) && r.some((c) => c !== null && c !== undefined && c !== ''));
  if (nonEmpty.length < 2) {
    errors.push('No non-empty rows');
    return { ok: false, errors, warnings };
  }

  // Header detection: row 0 has non-numeric text where row 1+ have numbers
  const row0 = nonEmpty[0];
  const row1 = nonEmpty[1];
  const hasHeader = row0.some((cell, i) => {
    const r0IsText = isNaN(parseNumberMaybe(cell)) && !(cell instanceof Date);
    const r1IsNumOrDate = !isNaN(parseNumberMaybe(row1[i])) || row1[i] instanceof Date;
    return r0IsText && r1IsNumOrDate;
  });
  const dataRows = hasHeader ? nonEmpty.slice(1) : nonEmpty;
  const headers = hasHeader
    ? row0.map((c) => (c == null ? '' : String(c)))
    : row0.map((_, i) => `col${i + 1}`);

  // Try two column layouts:
  //   A: single datetime column (first col) + value (last col)
  //   B: separate date + time columns (first two) + value (last col)
  const tryLayoutA = () => dataRows.map((r) => {
    const ts = parseDateMaybe(r[0]);
    const v = parseNumberMaybe(r[r.length - 1]);
    return { ts, v, ok: ts && !isNaN(v) };
  });
  const tryLayoutB = () => dataRows.map((r) => {
    if (r.length < 3) return { ok: false };
    const datePart = r[0] instanceof Date ? r[0].toISOString().slice(0, 10) : String(r[0] || '').trim();
    const timePart = r[1] instanceof Date ? r[1].toISOString().slice(11, 19) : String(r[1] || '').trim();
    const ts = parseDateMaybe(`${datePart} ${timePart}`);
    const v = parseNumberMaybe(r[r.length - 1]);
    return { ts, v, ok: ts && !isNaN(v) };
  });
  const a = tryLayoutA();
  const b = tryLayoutB();
  const aOk = a.filter((x) => x.ok).length;
  const bOk = b.filter((x) => x.ok).length;
  const chosen = aOk >= bOk ? { layout: 'single_datetime', rows: a, ok: aOk } : { layout: 'date_and_time', rows: b, ok: bOk };

  if (chosen.ok < 2) {
    errors.push('Could not parse timestamps from the first column(s)');
    return { ok: false, errors, warnings, hasHeader, headers };
  }

  const validRows = chosen.rows.filter((r) => r.ok);

  // Sort ascending (some files are reversed)
  validRows.sort((x, y) => x.ts - y.ts);

  // Interval detection — dominant delta in minutes between consecutive stamps
  const deltas = [];
  for (let i = 1; i < validRows.length; i++) {
    const d = Math.round((validRows[i].ts - validRows[i - 1].ts) / 60000);
    if (d > 0) deltas.push(d);
  }
  const freq = {};
  deltas.forEach((d) => { freq[d] = (freq[d] || 0) + 1; });
  const dominant = Object.entries(freq).sort((a, b) => b[1] - a[1])[0];
  const intervalMinutes = dominant ? Number(dominant[0]) : null;
  const consistency = dominant ? dominant[1] / deltas.length : 0;

  if (!intervalMinutes) {
    errors.push('Could not detect a time interval');
  }
  if (consistency < 0.95 && intervalMinutes) {
    warnings.push(`Interval consistency ${(consistency * 100).toFixed(1)}% — some rows have irregular spacing (DST transitions allowed)`);
  }

  // Range + row count
  const timeStart = validRows[0].ts;
  const timeEnd = validRows[validRows.length - 1].ts;

  // Gap detection — compare actual row count vs expected
  let expectedRows = null;
  let hasGaps = false;
  if (intervalMinutes) {
    expectedRows = Math.round((timeEnd - timeStart) / (intervalMinutes * 60000)) + 1;
    if (validRows.length < expectedRows - 2) { // allow 1 DST gap
      hasGaps = true;
      warnings.push(`${expectedRows - validRows.length} row(s) missing vs expected — data has gaps`);
    }
  }

  // Value stats
  const values = validRows.map((r) => r.v);
  const minV = Math.min(...values);
  const maxV = Math.max(...values);
  const meanV = values.reduce((s, v) => s + v, 0) / values.length;
  if (minV < 0) warnings.push(`Negative values present (min = ${minV.toFixed(2)})`);

  return {
    ok: errors.length === 0,
    errors,
    warnings,
    hasHeader,
    headers,
    layout: chosen.layout,
    intervalMinutes,
    intervalConsistency: consistency,
    timeStart,
    timeEnd,
    rowCount: validRows.length,
    expectedRows,
    hasGaps,
    stats: { min: minV, max: maxV, mean: meanV },
    parsedData: validRows,
  };
};

// --- Component --------------------------------------------------------------

const TimeseriesImporter = ({ projectId, onImported, onCancel }) => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);

  // Metadata form (editable by user before import)
  const [name, setName] = useState('');
  const [sourceUnit, setSourceUnit] = useState('kW');       // unit the file values are in
  const [sourceCapacity, setSourceCapacity] = useState('');  // nameplate AC capacity the file was modelled for
  const [timezone, setTimezone] = useState(guessTimezone());
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);

  const reset = () => {
    setFile(null);
    setAnalysis(null);
    setError(null);
    setName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChosen = async (f) => {
    if (!f) return;
    const lower = f.name.toLowerCase();
    const ext = ALLOWED_EXTENSIONS.find((e) => lower.endsWith(e));
    if (!ext) {
      setError(`Unsupported file type. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`);
      return;
    }
    setFile(f);
    setError(null);
    setAnalysis(null);
    setParsing(true);
    try {
      let rows;
      if (ext === '.xlsx' || ext === '.ods') {
        rows = await readSpreadsheetFile(f);
      } else {
        rows = await readCsvFile(f);
      }
      const result = analyzeRows(rows);
      setAnalysis(result);
      setName(f.name.replace(/\.[^.]+$/, ''));
    } catch (err) {
      setError('Parse failed: ' + (err.message || String(err)));
    } finally {
      setParsing(false);
    }
  };

  const handleImport = async () => {
    if (!analysis || !analysis.ok || !file) return;
    setUploading(true);
    setError(null);
    try {
      // Convert source capacity to MW for normalisation
      const srcCap = parseFloat(sourceCapacity);
      const srcCapMw = sourceUnit === 'kW' ? srcCap / 1000 : srcCap;

      // Normalise all values to per-1-MW: divide raw values by source capacity (in same unit)
      // e.g. PVsyst 60 MWac file with peak 55000 kW → 55000 / 60000 = 0.917 per MW
      const divisor = sourceUnit === 'kW' ? srcCap : srcCap * 1000; // divisor in kW (same as raw values)
      // But raw values might be in MW if sourceUnit is MW, so divisor = srcCap in the source unit
      const normDivisor = srcCap; // raw values are in sourceUnit, divide by source capacity in same unit

      // Serialize + normalise: [UTC ISO string, normalised value per 1 MW]
      const data = analysis.parsedData.map((r) => {
        const d = r.ts;
        const dt = DateTime.fromObject({
          year: d.getFullYear(),
          month: d.getMonth() + 1,
          day: d.getDate(),
          hour: d.getHours(),
          minute: d.getMinutes(),
          second: d.getSeconds(),
        }, { zone: timezone });
        const normValue = normDivisor > 0 ? r.v / normDivisor : r.v;
        return [dt.toUTC().toISO(), normValue];
      });
      const body = {
        metadata: {
          name: name.trim() || file.name,
          source_file: file.name,
          unit: sourceUnit,
          reference_capacity_mw: 1.0,  // always 1.0 since we normalised
          source_capacity: srcCap,
          source_capacity_mw: srcCapMw,
          source_unit: sourceUnit,
          interval_minutes: analysis.intervalMinutes,
          timezone,
          has_gaps: analysis.hasGaps,
          notes: notes.trim() || null,
        },
        data,
      };
      const res = await fetch(`${API}/api/projects/${projectId}/timeseries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.error || `HTTP ${res.status}`);
      }
      const created = await res.json();
      if (onImported) onImported(created);
      reset();
    } catch (err) {
      setError('Import failed: ' + (err.message || String(err)));
    } finally {
      setUploading(false);
    }
  };

  // Style helpers
  const box = {
    background: '#0f172a',
    border: '1px dashed #334155',
    borderRadius: 6,
    padding: 20,
    textAlign: 'center',
  };

  if (!file) {
    return (
      <div style={box}>
        <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 12 }}>
          Import a time series profile
        </div>
        <button
          className="btn-accent"
          onClick={() => fileInputRef.current?.click()}
        >
          Select file (CSV / XLSX / ODS)
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.tsv,.txt,.xlsx,.ods"
          onChange={(e) => handleFileChosen(e.target.files?.[0])}
          style={{ display: 'none' }}
        />
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 10 }}>
          Expected: date/time column(s) + value column. Header row auto-detected.
        </div>
      </div>
    );
  }

  if (parsing) {
    return (
      <div style={box}>
        <div style={{ color: '#94a3b8', fontSize: 13 }}>Parsing {file.name}…</div>
      </div>
    );
  }

  if (error && !analysis) {
    return (
      <div style={{ ...box, borderColor: '#dc2626' }}>
        <div style={{ color: '#fecaca', fontSize: 13, marginBottom: 10 }}>{error}</div>
        <button className="btn-card" onClick={reset}>Try another file</button>
      </div>
    );
  }

  if (!analysis) return null;

  const intervalLabel = analysis.intervalMinutes
    ? (analysis.intervalMinutes === 60 ? 'hourly'
        : analysis.intervalMinutes === 15 ? '15-minute'
        : analysis.intervalMinutes === 30 ? '30-minute'
        : analysis.intervalMinutes === 5 ? '5-minute'
        : `${analysis.intervalMinutes}-min`)
    : 'unknown';

  // Preview timestamps in the user-selected zone so the UI matches the file contents
  const fmtTs = (d) => {
    if (!d) return '—';
    return DateTime.fromObject({
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      day: d.getDate(),
      hour: d.getHours(),
      minute: d.getMinutes(),
      second: d.getSeconds(),
    }, { zone: timezone }).toFormat('yyyy-MM-dd HH:mm');
  };

  const allOk = analysis.ok && analysis.errors.length === 0;

  return (
    <div style={{
      background: '#0f172a',
      border: '1px solid #334155',
      borderRadius: 6,
      padding: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', flex: 1 }}>
          {file.name}
        </div>
        <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: "'JetBrains Mono', monospace" }}>
          {(file.size / 1024).toFixed(1)} KB
        </div>
        <button className="btn-card" onClick={reset}>Change file</button>
      </div>

      {/* Validation checklist */}
      <div style={{
        background: '#1e293b',
        border: '1px solid #334155',
        borderRadius: 4,
        padding: 12,
        marginBottom: 12,
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 12,
        color: '#e2e8f0',
        lineHeight: 1.6,
      }}>
        <div>
          <span style={{ color: '#4ecca3' }}>✓</span> Format detected: {analysis.layout === 'single_datetime' ? 'CSV/XLSX with single datetime column' : 'CSV/XLSX with separate date + time columns'}{analysis.hasHeader ? ' + header row' : ''}
        </div>
        <div>
          <span style={{ color: analysis.intervalMinutes ? '#4ecca3' : '#dc2626' }}>{analysis.intervalMinutes ? '✓' : '✗'}</span> Time interval: {intervalLabel}{analysis.intervalMinutes ? ` (${analysis.intervalMinutes} min)` : ''}, {(analysis.intervalConsistency * 100).toFixed(1)}% consistent
        </div>
        <div>
          <span style={{ color: '#4ecca3' }}>✓</span> Range: {fmtTs(analysis.timeStart)} → {fmtTs(analysis.timeEnd)}
        </div>
        <div>
          <span style={{ color: analysis.hasGaps ? '#fbbf24' : '#4ecca3' }}>{analysis.hasGaps ? '⚠' : '✓'}</span> Rows: {analysis.rowCount}{analysis.expectedRows ? ` (expected ${analysis.expectedRows})` : ''}{analysis.hasGaps ? ' — gaps detected' : ''}
        </div>
        <div>
          <span style={{ color: '#4ecca3' }}>✓</span> Values: min={analysis.stats.min.toFixed(2)}, max={analysis.stats.max.toFixed(2)}, mean={analysis.stats.mean.toFixed(2)}
        </div>
        {analysis.warnings.map((w, i) => (
          <div key={i} style={{ color: '#fbbf24' }}>⚠ {w}</div>
        ))}
        {analysis.errors.map((e, i) => (
          <div key={i} style={{ color: '#dc2626' }}>✗ {e}</div>
        ))}
      </div>

      {/* Metadata form */}
      <div style={{
        background: '#1e293b',
        border: '1px solid #334155',
        borderRadius: 4,
        padding: 12,
        marginBottom: 12,
      }}>
        <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
          Source parameters
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 12 }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Series name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Values in the file are in <span style={{ color: '#dc2626' }}>*</span></label>
            <select value={sourceUnit} onChange={(e) => setSourceUnit(e.target.value)}>
              <option value="kW">kWac</option>
              <option value="MW">MWac</option>
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Modelled for nameplate capacity <span style={{ color: '#dc2626' }}>*</span></label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="number"
                step="0.001"
                min="0.001"
                value={sourceCapacity}
                onChange={(e) => setSourceCapacity(e.target.value)}
                placeholder="e.g. 60"
                style={{ flex: 1 }}
              />
              <span style={{ fontSize: 12, color: '#94a3b8' }}>{sourceUnit === 'kW' ? 'kWac' : 'MWac'}</span>
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Timezone <span style={{ color: '#dc2626' }}>*</span></label>
            <select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
              {COMMON_TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </div>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Notes (optional)</label>
          <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. PVsyst export, Doringberg location" />
        </div>
      </div>

      {error && (
        <div style={{
          background: '#7f1d1d',
          color: '#fecaca',
          padding: '8px 12px',
          borderRadius: 4,
          marginBottom: 10,
          fontSize: 12,
        }}>{error}</div>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button className="btn-card" onClick={onCancel || reset} disabled={uploading}>Cancel</button>
        <button
          className="btn-accent"
          onClick={handleImport}
          disabled={!allOk || uploading || !name.trim() || !sourceCapacity || parseFloat(sourceCapacity) <= 0}
        >
          {uploading ? 'Importing…' : `Import ${analysis.rowCount} rows`}
        </button>
      </div>
    </div>
  );
};

export default TimeseriesImporter;
